import type { PartiallyRequired, StrictOverwrite } from '@skedulo/horizon-types'
import { UI } from '@skedulo/protobuf-web'

export const { UPSERT: SCHEMA_UPSERT, DELETE: SCHEMA_DELETE } =
  UI.API.SchemaChangeType
export type SchemaChangeFromUI = typeof SCHEMA_UPSERT | typeof SCHEMA_DELETE

export type IInitiateSchemaSyncRequest = StrictOverwrite<
  PartiallyRequired<UI.API.InitiateSchemaSyncRequest, 'change' | 'schema'>,
  { change: SchemaChangeFromUI }
>
export type IInitiateSchemaSyncResponse = UI.API.InitiateSchemaSyncResponse

export type IExecuteBulkActionRequest = StrictOverwrite<
  PartiallyRequired<
    UI.API.ExecuteBulkActionRequest,
    'resourceId' | 'objectIds' | 'action'
  >,
  {
    action:
      | 'notify'
      | 'delete'
      | 'lock'
      | 'unlock'
      | 'cancel'
      | 'deallocate'
      | 'unschedule'
  }
>
export type IExecuteBulkActionResponse = UI.API.ExecuteBulkActionResponse

export type IFilter = UI.CRUD.Filter

export type IFilterGetRequest = PartiallyRequired<
  UI.API.GetFilterRequest,
  'uid'
>

export type IFilterCreateRequest = StrictOverwrite<
  PartiallyRequired<UI.API.NewFilterRequest, 'filter'>,
  {
    filter: PartiallyRequired<IFilter, 'name'>
  }
>

export type IAction = UI.API.ACTION.Action
export type IListActionsRequest = PartiallyRequired<
  UI.API.ACTION.ListActionsRequest,
  'resourceName'
>
export type IGetActionRequest =
  | (PartiallyRequired<UI.API.ACTION.GetActionRequest, 'id'> & {
      actionKey?: never
    })
  | (PartiallyRequired<UI.API.ACTION.GetActionRequest, 'actionKey'> & {
      id?: never
    })

export type IFilterPutRequest = PartiallyRequired<
  UI.API.PutFilterRequest,
  'filter'
>

export type IFilterDelRequest = PartiallyRequired<
  UI.API.DelFilterRequest,
  'uid'
>

export type IFilterGetManyRequest = PartiallyRequired<
  UI.API.GetFiltersRequest,
  'limit' // TODO: Limit is not really required but maybe a sensible FE safeguard? Consider removing.
>

export type IGetRelevantFilterRequest = PartiallyRequired<
  UI.API.GetRelevantFilterRequest,
  'resourceId'
>

export type INavNode = UI.CRUD.NavNode
export type ILooseNavNode = StrictOverwrite<
  PartiallyRequired<INavNode, 'label' | 'ref' | 'destination' | 'nodes'>,
  {
    nodes?: ILooseNavNode[]
  }
>

export type INav = UI.CRUD.Nav

export type INavGetRequest = PartiallyRequired<UI.API.GetNavRequest, 'uid'>

export type INavGetManyRequest = PartiallyRequired<
  UI.CRUD.GetNavsRequest,
  'limit' // TODO: Limit is not really required but maybe a sensible FE safeguard? Consider removing.
>

export type INavCreateRequest = StrictOverwrite<
  PartiallyRequired<UI.API.NewNavRequest, 'nav'>,
  {
    nav: StrictOverwrite<
      PartiallyRequired<INav, 'nodes'>,
      {
        nodes?: ILooseNavNode[]
      }
    >
  }
>

export type INavPutRequest = StrictOverwrite<
  PartiallyRequired<UI.API.PutNavRequest, 'nav'>,
  {
    nav: StrictOverwrite<
      PartiallyRequired<INav, 'uid' | 'nodes'>,
      {
        nodes?: ILooseNavNode[]
      }
    >
  }
>

export type INavDelRequest = PartiallyRequired<UI.API.DelNavRequest, 'uid'>

export type IGetDestinationsRequest = PartiallyRequired<
  UI.API.GetDestinationsRequest,
  'destination'
>
export type IGetDestinationsResponse = UI.API.GetDestinationsResponse

export type IList = UI.CRUD.List
export type IListPartial = PartiallyRequired<
  IList,
  'name' | 'resourceId' | 'ordering' | 'depth'
>

export type IListGetRequest = PartiallyRequired<UI.API.GetListRequest, 'uid'>

export type IListCreateRequest = StrictOverwrite<
  PartiallyRequired<UI.API.NewListRequest, 'list'>,
  {
    list: IListPartial
  }
>

export type IListPutRequest = StrictOverwrite<
  PartiallyRequired<UI.API.PutListRequest, 'list'>,
  {
    list: IList
  }
>

export type IListDelRequest = PartiallyRequired<UI.API.DelListRequest, 'uid'>

export type IListGetManyRequest = PartiallyRequired<
  UI.API.GetListsRequest,
  'limit' // TODO: Limit is not really required but maybe a sensible FE safeguard? Consider removing.
>

export type IGetRelevantListRequest = PartiallyRequired<
  UI.API.GetRelevantListRequest,
  'resourceId'
>

export type IListConfiguration = UI.CRUD.ListConfiguration

export type IGetListConfigurationRequest = UI.API.GetListConfigurationRequest

export type IApiResource = UI.CRUD.Resource

export type IResourceGetRequest = PartiallyRequired<
  UI.API.GetResourceRequest,
  'uid'
>

export type IResourceCreateRequest = StrictOverwrite<
  PartiallyRequired<UI.API.NewResourceRequest, 'resource'>,
  {
    resource: PartiallyRequired<
      IApiResource,
      'name' | 'resource' | 'datasource'
    >
  }
>

export type IResourcePutRequest = PartiallyRequired<
  UI.API.PutResourceRequest,
  'resource'
>

export type IResourceDelRequest = PartiallyRequired<
  UI.API.DelResourceRequest,
  'uid'
>

export type IResourceGetManyRequest = PartiallyRequired<
  UI.API.GetResourcesRequest,
  'limit'
>

export type IApiTemplate = UI.CRUD.Template
export type ITemplateGetRequest = PartiallyRequired<IApiTemplate, 'uid'>

export type ITemplateCreateRequest = StrictOverwrite<
  PartiallyRequired<UI.API.NewTemplateRequest, 'template'>,
  { template: PartiallyRequired<IApiTemplate, 'name' | 'kind'> }
>

export type ITemplatePutRequest = PartiallyRequired<
  UI.API.PutTemplateRequest,
  'template'
>

export type ITemplateDelRequest = PartiallyRequired<
  UI.API.DelTemplateRequest,
  'uid'
>

export type ITemplateGetManyRequest = PartiallyRequired<
  UI.API.GetTemplatesRequest,
  'limit'
>

export type IValidation = UI.CRUD.Validation

export type IValidationGetRequest = PartiallyRequired<
  UI.API.GetValidationRequest,
  'uid'
>

export type IValidationDeleteRequest = PartiallyRequired<
  UI.API.DelValidationRequest,
  'uid'
>

export type IValidationCreateRequest = StrictOverwrite<
  PartiallyRequired<UI.API.NewValidationRequest, 'validation'>,
  { validation: PartiallyRequired<IValidation, 'name' | 'jsonSchema'> }
>

export type IValidationPutRequest = PartiallyRequired<
  UI.API.PutValidationRequest,
  'validation'
>

export type IValidationGetManyRequest = PartiallyRequired<
  UI.API.GetValidationsRequest,
  'limit' | 'name'
>

export type IValidateDataRequest = PartiallyRequired<
  UI.API.ValidateDataRequest,
  'name' | 'data'
>

export interface IValidationError {
  code: string
  message: string
  path: string
}

// `validated` is the decoded numeric UI.API.Validated enum, not the raw string
// the REST endpoint returns — consumers compare against UI.API.Validated.*.
export type IValidateDataResponse = {
  validated: UI.API.Validated
  reasons: string[]
  errors: IValidationError[]
}

export type IViewOverride = UI.CRUD.ViewOverride

export type IViewOverrideGetRequest = PartiallyRequired<
  UI.API.GetViewOverrideRequest,
  'uid'
>

export type IViewOverrideCreateRequest = StrictOverwrite<
  PartiallyRequired<UI.API.NewViewOverrideRequest, 'viewOverride'>,
  {
    viewOverride: PartiallyRequired<
      IViewOverride,
      'viewId' | 'data' | 'name' | 'userId' | 'tenantId'
    >
  }
>

export type IViewOverridePutRequest = PartiallyRequired<
  UI.API.PutViewOverrideRequest,
  'viewOverride'
>

export type IViewOverrideDelRequest = PartiallyRequired<
  UI.API.DelViewOverrideRequest,
  'uid'
>

export type IViewOverrideGetManyRequest = PartiallyRequired<
  UI.API.GetViewOverridesRequest,
  'limit' | 'viewId'
>

export type IApiPage = UI.CRUD.Page
export type IPagePutRequest = PartiallyRequired<UI.API.PutPageRequest, 'page'>

export type IPageDelRequest = PartiallyRequired<UI.API.DelPageRequest, 'uid'>

export type IPageGetRequest = UI.API.GetPageRequest

export type IPageCreateRequest = StrictOverwrite<
  PartiallyRequired<UI.API.NewPageRequest, 'page'>,
  { page: PartiallyRequired<IApiPage, 'name'> }
>

export type IPageGetManyRequest = PartiallyRequired<
  UI.API.GetPagesRequest,
  'limit'
>

export type IApiPageTemplate = PartiallyRequired<
  UI.API.PageTemplate,
  'page' | 'template'
>

export type IPageTemplateGetRequest = PartiallyRequired<
  UI.API.GetPageTemplateRequest,
  'slug'
>
